import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { FEATURE_FLAG_NAME } from "common/constants";
import { getPath } from "common/routing";
import { track } from "../../utils/tracking";
import { AuthContext } from "../Authorization/AuthContext";
import { AuthContextType } from "../Authorization/types";
import { ButtonLink } from "../Common/ButtonLink";
import { Button } from "../Common/Buttons/Button";
import DropdownMenu, { ActionsProps } from "../Inputs/DropdownMenu";
import { PublicUserProfilePlaceholder } from "./__styles__/Layout";
import { PublicUserProfileCircleButton } from "./__styles__/Layout";
import { getPublicUserInitials } from "./utils";

type HeaderProfileProps = {
  inMenu?: boolean;
  onClickSignIn: () => void;
};

export const HeaderProfile = ({
  inMenu = false,
  onClickSignIn,
}: HeaderProfileProps) => {
  const { isFeatureEnabled, publicUser, loadingAuthorizedEntity } =
    useContext(AuthContext);

  if (!isFeatureEnabled(FEATURE_FLAG_NAME.PUBLIC_USERS)) return null;

  if (loadingAuthorizedEntity) return <PublicUserLoading inMenu={inMenu} />;
  if (publicUser)
    return <PublicUserProfile inMenu={inMenu} publicUser={publicUser} />;
  return <SignInButton onClick={onClickSignIn} inMenu={inMenu} />;
};

const PublicUserLoading = ({ inMenu = false }: { inMenu?: boolean }) => {
  if (inMenu) {
    return null;
  }
  return (
    <PublicUserProfilePlaceholder data-testid="public-user-profile-placeholder" />
  );
};

const PublicUserProfile = ({
  inMenu,
  publicUser,
}: {
  inMenu: boolean;
  publicUser: NonNullable<AuthContextType["publicUser"]>;
}) => {
  const { t } = useTranslation();
  const history = useHistory();

  const actions: Array<ActionsProps> = [
    {
      label: t("common-submissions-upper"),
      onClick: () => history.push(getPath("mySubmissions")),
    },
    {
      label: t("sign-out"),
      onClick: () => {
        track("Public user logged out");
        window.location.href = getPath("logout");
      },
    },
  ];

  if (inMenu) {
    return <PublicUserProfileInMenu actions={actions} />;
  }
  return (
    <PublicUserProfileInHeader publicUser={publicUser} actions={actions} />
  );
};

/**
 * - Displays inside the navigation menu that appears on small screens
 * - Lists a button for each action available to logged-in public users
 */
const PublicUserProfileInMenu = ({
  actions,
}: {
  actions: Array<ActionsProps>;
}) => {
  return (
    <>
      {actions.map(({ label, ...rest }) => (
        <ButtonLink
          key={label as string}
          size="medium"
          styleVariant="inverted"
          {...rest}
        >
          {label}
        </ButtonLink>
      ))}
    </>
  );
};

/**
 * - Displays in the page header for medium+ screens
 * - Shows a circular dropdown button with user initials, which opens a list of
 *   links for each action available to logged-in public users
 */
const PublicUserProfileInHeader = ({
  publicUser,
  actions,
}: {
  publicUser: NonNullable<AuthContextType["publicUser"]>;
  actions: Array<ActionsProps>;
}) => {
  return (
    <DropdownMenu
      actions={actions}
      customButton={({ onPress }) => (
        <PublicUserProfileCircleButton
          data-testid="public-user-profile-button"
          onClick={onPress}
        >
          {getPublicUserInitials(publicUser)}
        </PublicUserProfileCircleButton>
      )}
      position="below-left"
    />
  );
};

const SignInButton = ({
  inMenu = false,
  onClick,
}: {
  inMenu?: boolean;
  onClick: () => void;
}) => {
  const { t } = useTranslation();

  //expanded menu small screens
  if (inMenu) {
    return (
      <ButtonLink onClick={onClick} size="medium" styleVariant="inverted">
        {t("sign-in")}
      </ButtonLink>
    );
  }

  //header nav large screens
  return (
    <Button
      onPress={onClick}
      size="small"
      variant="secondary"
      title={t("sign-in")}
    />
  );
};
